import { isString } from 'fitify-types/src/type-guards/common'
import { z } from 'fitify-types/src/zod'
import { clearItem, getItem, setItem } from 'fitify-ui/src/utils/storage'
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import type { EmailUser } from '../types/app'

type EmailUserContextData = {
  emailUser?: EmailUser
  resetUser: () => void
  validateAndSetToken: (args: { displayName?: string; token: string }) => void
}

const TOKEN_STORAGE_KEY = '@fitify/funnelToken'

const storagePayloadSchema = z.object({
  displayName: z.string().nullishCoalescingUndefined(),
  token: z.string(),
})

const tokenPayloadSchema = z.object({
  email: z.string(),
  sub: z.string(),
})

const EmailUserContext = createContext<EmailUserContextData>({
  // eslint-disable-next-line no-empty-function
  resetUser: () => {},
  // eslint-disable-next-line no-empty-function
  validateAndSetToken: () => {},
})

const parseToken = (token: unknown): { email: string; uid: string } => {
  if (isString(token)) {
    // Validate the token and get the user ID
    const tokenParts = token.split('.')
    if (tokenParts.length !== 3) {
      throw new Error('Invalid token')
    }
    const [_, base64Payload] = tokenParts
    const { sub: uid, email } = tokenPayloadSchema.parse(
      JSON.parse(atob(base64Payload))
    )
    return { uid, email }
  }
  throw new Error('Invalid token')
}

const parseStorageData = (): EmailUser | undefined => {
  const storageToken = getItem('session', TOKEN_STORAGE_KEY)
  if (isString(storageToken)) {
    try {
      const { displayName, token } = storagePayloadSchema.parse(
        JSON.parse(storageToken)
      )
      const { email, uid } = parseToken(token)
      return { displayName, email, token, uid }
    } catch {
      return undefined
    }
  }
  return undefined
}

export const EmailUserContextProvider = ({ children }: PropsWithChildren) => {
  const [emailUser, setEmailUser] = useState<EmailUser | undefined>(
    parseStorageData
  )

  const resetUser = useCallback(() => {
    setEmailUser(undefined)
    clearItem('session', TOKEN_STORAGE_KEY)
  }, [])

  const validateAndSetToken = useCallback<
    EmailUserContextData['validateAndSetToken']
  >(
    ({ displayName, token }) => {
      try {
        const { email, uid } = parseToken(token)
        setEmailUser({ displayName, email, token, uid })
        setItem(
          'session',
          TOKEN_STORAGE_KEY,
          JSON.stringify({ displayName, token })
        )
      } catch (error) {
        console.error(error)
        resetUser()
      }
    },
    [resetUser]
  )

  const value = useMemo(() => {
    return {
      emailUser,
      resetUser,
      validateAndSetToken,
    }
  }, [emailUser, resetUser, validateAndSetToken])

  return (
    <EmailUserContext.Provider value={value}>
      {children}
    </EmailUserContext.Provider>
  )
}

export const useEmailUserContext = () => {
  const context = useContext(EmailUserContext)
  if (!context) {
    throw new Error(
      'useEmailUserContext must be used within a EmailUserContextProvider'
    )
  }
  return context
}
